import React, {useEffect, useState} from "react";
import "../Login/Forms.scss";
import {useController, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {submitForm, UserVerificationData} from "../../utils/helpers/submitForm";
import DatePicker from 'react-datepicker'
import {useNavigate} from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import {format} from "date-fns";
import {fetchFromContentProxy} from "../../utils/helpers/fetchFromContentProxy";
import {LogoContainer} from "../../components/Logo/LogoContainer";
import { environmentVariables } from '../../utils/helpers/environmentVariables';
import {getServerRefusal, PASSWORD_PATTERN, PASSWORD_RULE_MESSAGE} from '../../utils/helpers/passwordPolicy';

interface Country {
    contentfulId: string;
    contentfulType: string;
    created: string;
    iso: string;
    name: string;
    updated: string;
}

const RegisterPage = () => {
    const {register, getValues, handleSubmit, formState: {errors}, control} = useForm({mode: 'onBlur'});
    const navigate = useNavigate();
    const {field: {onChange, value}} = useController({name: 'dateOfBirth', control: control})
    const [countries, setCountries] = useState<Country[] | null>(null)
    // The reason the server gave for refusing the password, shown next to the field.
    const [passwordRefusal, setPasswordRefusal] = useState<string | null>(null)

    useEffect(() => {
        (async () => {
            const countryList: Country[] = await fetchFromContentProxy('country');
            setCountries(countryList)
        })()
    }, [])

    const onSubmit = async (data: any) => {
        let registerData = {
            ...data,
            applicationName: environmentVariables.applicationName(),
            gdprConsent: true,
            newsletterConsent: true,
            tAndCAccepted: true
        };
        const loginToast = toast.loading('Loading...');
        setPasswordRefusal(null)
        if (environmentVariables.registerUrl()) {
            registerData.dateOfBirth = format(registerData.dateOfBirth, 'yyyy-MM-dd')
            const registerResponse = await submitForm(registerData, environmentVariables.registerUrl())

            if (registerResponse && (registerResponse["status"] !== undefined && registerResponse["status"] === "success")) {
                await sendNotificationVerifyEmail(registerData)
                toast.dismiss(loginToast)
            } else {
                toast.dismiss(loginToast)
                // Show the server's reason: next to the field when it is about the password,
                // otherwise as a message. Only an unrecognised response gets the generic one.
                const refusal = getServerRefusal(registerResponse, 'password')
                if (refusal && refusal.onPasswordField) {
                    setPasswordRefusal(refusal.message)
                } else {
                    toast.error(refusal ? refusal.message : 'Server Error!', {
                        autoClose: refusal ? 5000 : 2000,
                        position: toast.POSITION.TOP_CENTER
                    });
                }
            }
        }
    }

    async function sendNotificationVerifyEmail(submitData: any) {
        const userVerificationData: UserVerificationData = {
            firstName: submitData.firstName,
            redirectURL: `${window.location.origin}/login`,
            emailTemplate: "InsightsUserVerification",
            recipient: submitData.emailAddress,
            tokenUrl: "email/generate-token",
            userName: submitData.emailAddress,
            operation: "VERIFY_USER"
        };
        // The account already exists by now, so only a confirmed send goes to the "verify your
        // e-mail" page; anything else tells the person the e-mail did not go out.
        let sent = false
        try {
            const emailResponse = await submitForm(userVerificationData, 'email/verify-user-email')
            sent = !!emailResponse && emailResponse['status'] === 'success'
        } catch (e) {
            sent = false
        }
        if (sent) {
            navigate("/email-verification");
        } else {
            toast.error('Your account was created and you can log in, but we could not send the verification e-mail.', {
                autoClose: 5000,
                position: toast.POSITION.TOP_CENTER
            });
        }
    }

    return (
        <div className="insightsBackgroundContainer">
            <LogoContainer extraMarginTop={true}/>
            <div className="registerFormContainer">
                <div className='insightsForm__infoContainer'>
                    <h1 className="insightsForm__title">Sign up for free.</h1>
                    <p className="insightsForm__text insightsForm__text-info">
                        Get rewarded for interacting with all things SailGP+ and earn exclusive
                        experiences, discounts and more.
                    </p>
                </div>
                <p className="insightsForm__text insightsForm__text-bold">Sign up with your e-mail address</p>
                <form className='insightsForm' onSubmit={handleSubmit(onSubmit)}>
                    <label className="insightsForm__label" htmlFor="firstName">
                        First Name
                    </label>
                    <input
                        className={`insightsForm__input ${
                            errors.firstName ? "insightsForm__input--error" : ""
                        } `}
                        type="text"
                        {...register("firstName", {
                            required: true,
                            pattern: /^[a-z ,.'-]+$/i
                        })}
                    />
                    {errors.firstName && errors.firstName.type === 'required' && (
                        <p className='insightsForm__error'>This field is required.</p>
                    )}
                    {errors.firstName && errors.firstName.type === 'pattern' && (
                        <p className='insightsForm__error'>Please enter a valid first name.</p>
                    )}

                    <label className="insightsForm__label" htmlFor="lastName">
                        Last Name
                    </label>
                    <input
                        className={`insightsForm__input ${
                            errors.lastName ? "insightsForm__input--error" : ""
                        } `}
                        type="text"
                        {...register("lastName", {
                            required: true,
                            pattern: /^[a-z ,.'-]+$/i
                        })}
                    />
                    {errors.lastName && errors.lastName.type === 'required' && (
                        <p className='insightsForm__error'>This field is required.</p>
                    )}
                    {errors.lastName && errors.lastName.type === 'pattern' && (
                        <p className='insightsForm__error'>Please enter a valid last name.</p>
                    )}


                    <label className="insightsForm__label" htmlFor="emailAddress">
                        Email Address
                    </label>
                    <input
                        className={`insightsForm__input ${
                            errors.emailAddress ? "insightsForm__input--error" : ""
                        } `}
                        type="text"
                        {...register("emailAddress", {
                            required: true,
                            pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
                        })}
                    />

                    {errors.emailAddress && errors.emailAddress.type === 'required' && (
                        <p className='insightsForm__error'>This field is required.</p>
                    )}
                    {errors.emailAddress && errors.emailAddress.type === 'pattern' && (
                        <p className='insightsForm__error'>Please enter a valid email address.</p>
                    )}
                    <label className="insightsForm__label" htmlFor="dateOfBirth">
                        Date of Birth
                    </label>
                    <DatePicker
                        name='dateOfBirth'
                        minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 120))}
                        selected={value}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode='select'
                        dateFormat={'dd/MM/yyyy'}
                        onChange={(date) => onChange(date)}
                        placeholderText='DD/MM/YYYY'
                        onKeyDown={(e) => {
                            e.preventDefault();
                        }}
                    />
                    <label className="insightsForm__label" htmlFor="country">Country</label>
                    <select
                        className={`insightsForm__input ${errors.country ? "insightsForm__input--error" : ""} `}
                        {...register('country', {required: true})}>
                        <option
                            value=""
                            className="select-option"
                        >
                            -- Please select --
                        </option>
                        {countries && countries.map(country => {
                            return <option key={country.contentfulId} value={country.iso}>
                                {country.name}
                            </option>
                        })}</select>
                    {errors.country && errors.country.type === 'required' && (
                        <p className='insightsForm__error'>This field is required.</p>
                    )}
                    <label className="insightsForm__label" htmlFor="password">
                        Password
                    </label>
                    <input
                        className={`insightsForm__input ${
                            errors.password ? "insightsForm__input--error" : ""
                        } `}
                        type="password"
                        {...register("password", {
                            required: true,
                            pattern: PASSWORD_PATTERN,
                            onChange: () => setPasswordRefusal(null),
                        })}
                    />
                    {errors.password && errors.password.type === 'required' && (
                        <p className='insightsForm__error'>This field is required.</p>
                    )}
                    {errors.password && errors.password.type === 'pattern' && (
                        <p className='insightsForm__error'>{PASSWORD_RULE_MESSAGE}</p>
                    )}
                    {passwordRefusal && !errors.password && (
                        <p className='insightsForm__error'>{passwordRefusal}</p>
                    )}

                    <p>By clicking 'Continue' you agree to hear from SailGP about the latest content, exclusive
                        competitions,
                        team news, tickets and occasional Partner offers via email in accordance with the SailGP &nbsp;
                        <a className="insightsForm__link"
                           target="_blank" rel="noreferrer"
                           href='https://sailgp.com/general/privacy/'>Privacy Policy</a> and &nbsp;
                        <a className="insightsForm__link"
                           target="_blank" rel="noreferrer"
                           href='https://sailgp.com/sailgpplus/terms/'>Data Retention Policy.</a>
                    </p>
                    <div className="insightsForm__buttonContainer">
                        <button
                            className={`insightsForm__submit ${
                                Object.keys(errors).length > 0
                                    ? "insightsForm__submit--disabled"
                                    : ""
                            }`}
                            onClick={handleSubmit(onSubmit)}
                            type="submit"
                            disabled={Object.keys(errors).length > 0}
                        >
                            <p className="insightsForm__email">Continue</p>
                            <img
                                className="insightsForm__icon"
                                src={
                                    Object.keys(errors).length > 0
                                        ? "/Sign_in_circle_disabled.svg"
                                        : "/Sign_in_circle_enabled.svg"
                                }
                                alt=""
                            />
                        </button>
                    </div>

                </form>
            </div>

        </div>
    );
};
export default RegisterPage;
